import { Component, ElementRef, HostListener, QueryList, ViewChildren } from '@angular/core';
import { CommunicationProtocolSocketService } from '@service/communication-protocol-socket.service';
import { CommunicationProtocolService } from '@service/communication-protocol.service';
import { MessageDialogService } from '@service/message-dialog.service';
import { VesselService } from '@service/vessel.service';
import { CommunicationProtocol, ProtocolType } from 'icreate/common/models/communication-protocol';
import { fromEvent, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { CommunicationProtocolDialogComponent } from './communication-protocol-dialog/communication-protocol-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { communicationTypes } from 'icreate/common/models/fu-unit';

@Component({
    selector: 'app-communication-protocols',
    templateUrl: './communication-protocols.component.html',
    styleUrl: './communication-protocols.component.css',
    standalone: false,
})
export class CommunicationProtocolsComponent {
    @ViewChildren('optionsCol') optionsCol: QueryList<ElementRef>;

    isLoading: boolean = false;
    isInfoMessageVisible: boolean = false;

    visibleProtocolOptions: CommunicationProtocol;

    openedModelOptions;
    optionsTop: string;
    optionsLeft: string;

    communicationProtocols: CommunicationProtocol[];

    private destroy$: Subject<void> = new Subject();

    constructor(
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageDialogService: MessageDialogService,
        private protocolService: CommunicationProtocolService,
        private protocolSocketService: CommunicationProtocolSocketService,
        private vesselService: VesselService
    ) {}

    @HostListener('document:click', [])
    clickOut() {
        this.visibleProtocolOptions = null;
        this.openedModelOptions = null;
    }

    async ngOnInit(): Promise<void> {
        this.protocolSocketService
            .protocolCreated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(newProtocol => {
                this.communicationProtocols.push(newProtocol);
                this.messageDialogService.openMessage('communication-protocols.created');
            });

        this.protocolSocketService
            .protocolUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe((protocol: CommunicationProtocol) => {
                const protocolIndex = this.communicationProtocols.findIndex(prot => prot.id == protocol.id);
                if (protocolIndex >= 0) {
                    this.communicationProtocols.splice(protocolIndex, 1, protocol);
                    this.messageDialogService.openMessage('communication-protocols.updated', {
                        name: protocol.name,
                    });
                }
            });

        this.protocolSocketService
            .protocolDeleted$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(protocol => {
                const protocolIndex = this.communicationProtocols.findIndex(prot => prot.id == protocol.id);
                if (protocolIndex >= 0) {
                    this.communicationProtocols.splice(protocolIndex, 1);
                    this.messageDialogService.openMessage('communication-protocols.deleted', {
                        name: protocol.name,
                    });
                }
            });

        try {
            this.isLoading = true;
            this.vesselService.setActiveVessel(null);
            this.communicationProtocols = await this.protocolService.getAll();
        } catch (err) {
            console.error(err);
        }
        this.isLoading = false;

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isInfoMessageVisible = false;
                    this.visibleProtocolOptions = null;
                    this.openedModelOptions = null;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getProtocolType(protocol: CommunicationProtocol) {
        const type = protocol.type;
        if (type == ProtocolType.POLLING) return 'communication-protocols.type.polling';
        else if (type == ProtocolType.RECEIVER) return 'communication-protocols.type.receiver';
        else if (type == ProtocolType.TRANSMITER) return 'communication-protocols.type.transmiter';
        else if (type == ProtocolType.RESPONDER) return 'communication-protocols.type.responder';
    }

    getBoardType(protocol: CommunicationProtocol) {
        const boardType = protocol.boardType;
        if (boardType == communicationTypes.lan) return 'dlg.lan';
        else if (boardType == communicationTypes.serial) return 'dlg.serial';
    }

    openOptions(protocol): void {
        this.visibleProtocolOptions = protocol;
    }

    async createProtocol() {
        const dialogRef = this.dialog.open(CommunicationProtocolDialogComponent, {
            disableClose: true,
            width: '474px',
            height: '284px',
            data: { protocolList: this.communicationProtocols },
        });
        const newProtocol = await lastValueFrom(dialogRef.afterClosed());
        if (newProtocol) {
            await this.protocolService.create(newProtocol);
        }
    }

    async edit(protocol: CommunicationProtocol) {
        const dialogRef = this.dialog.open(CommunicationProtocolDialogComponent, {
            disableClose: true,
            width: '474px',
            height: '284px',
            data: { protocolList: this.communicationProtocols, protocol: protocol },
        });
        const updatedProtocol = await lastValueFrom(dialogRef.afterClosed());
        if (updatedProtocol) {
            await this.protocolService.update({ ...updatedProtocol, id: protocol.id, createdAt: protocol.createdAt });
        }
    }

    async deleteProtocol(protocol: CommunicationProtocol) {
        const deleteTitle = this.translate.instant('communication-protocols.delete.title', { title: protocol.name });
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: deleteTitle,
                message: 'communication-protocols.delete.subLabel',
                discardButton: 'dlg.cancel',
                saveButton: 'dlg.delete',
                width: '452px',
            },
            disableClose: true,
        });
        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            await this.protocolService.deleteComm(protocol);
        }
    }
}
